/**
 * AttributeLabelingPanel — fill in clinical attributes for known-positive findings.
 *
 * Used when worklist.label_schema.type === 'attribute_labeling'.
 * labels_to_assign contains only the confirmed-positive conditions for this case.
 * For each condition, renders checkbox groups for every attribute defined in
 * label_schema.attribute_definitions[condition].
 *
 * Persists one DynamoDB label per condition:
 *   label_key   = condition name  (e.g. "Hepatic trauma")
 *   label_value = JSON string     (e.g. '{"AAST Grade":["III"],"Location":["Right lobe"]}')
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  useCaseLabelsQuery,
  useSaveLabelMutation,
  useDeleteLabelMutation,
} from "../../hooks/useLabelingQueries";
import { NavigationButtons } from "../common";
import { WorkListButton } from "./WorkListButton";

interface AttributeLabelingPanelProps {
  projectId: string;
  caseId: string;
  studyInstanceUid: string;
  labelsToAssign: string[]; // confirmed-positive conditions
  attributeDefinitions: Record<string, Record<string, string[]>>; // condition → attrName → options
  onNext: () => void;
  onPrevious: () => void;
  onNextUnlabeled: () => void;
  onWorkList?: () => void;
  hasNext: boolean;
  hasPrevious: boolean;
  hasNextUnlabeled: boolean;
  currentIndex: number;
  totalCases: number;
}

// localAttr[condition][attrName] = selected options
type LocalAttr = Record<string, Record<string, string[]>>;

function parseStoredAttrs(labelValue: string): Record<string, string[]> {
  try {
    return JSON.parse(labelValue);
  } catch {
    return {};
  }
}

export function AttributeLabelingPanel({
  projectId,
  caseId,
  studyInstanceUid,
  labelsToAssign,
  attributeDefinitions,
  onNext,
  onPrevious,
  onNextUnlabeled,
  onWorkList,
  hasNext,
  hasPrevious,
  hasNextUnlabeled,
  currentIndex,
  totalCases,
}: AttributeLabelingPanelProps) {
  const { data: existingLabels } = useCaseLabelsQuery(projectId, caseId);
  const saveMutation = useSaveLabelMutation(projectId);
  const deleteMutation = useDeleteLabelMutation(projectId);

  const [localAttr, setLocalAttr] = useState<LocalAttr>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const loadedCaseRef = useRef<string>("");

  // Populate from existing DynamoDB labels when case loads
  useEffect(() => {
    if (!existingLabels || loadedCaseRef.current === caseId) return;
    loadedCaseRef.current = caseId;

    const initial: LocalAttr = {};
    for (const cond of labelsToAssign) {
      const stored = existingLabels.labels?.find((l) => l.label_key === cond);
      initial[cond] = stored ? parseStoredAttrs(stored.label_value) : {};
    }
    setLocalAttr(initial);
  }, [existingLabels, caseId, labelsToAssign]);

  // Reset on case navigation
  useEffect(() => {
    loadedCaseRef.current = "";
    setLocalAttr({});
    setSaving({});
  }, [caseId]);

  const handleConditionSave = useCallback(
    async (condition: string, updatedAttr?: LocalAttr) => {
      const attrs = (updatedAttr ?? localAttr)[condition] ?? {};
      const isEmpty = Object.values(attrs).every((v) => v.length === 0);
      setSaving((prev) => ({ ...prev, [condition]: true }));
      try {
        if (isEmpty) {
          await deleteMutation.mutateAsync({
            project_id: projectId,
            case_id: caseId,
            label_key: condition,
          });
        } else {
          await saveMutation.mutateAsync({
            project_id: projectId,
            case_id: caseId,
            study_instance_uid: studyInstanceUid,
            label_key: condition,
            label_value: JSON.stringify(attrs),
            notes: "",
          });
        }
      } catch (e) {
        console.error("Failed to save attributes:", e);
      } finally {
        setSaving((prev) => ({ ...prev, [condition]: false }));
      }
    },
    [localAttr, projectId, caseId, studyInstanceUid, saveMutation, deleteMutation],
  );

  const handleToggleAndSave = useCallback(
    (condition: string, attrName: string, option: string) => {
      setLocalAttr((prev) => {
        const current = prev[condition]?.[attrName] ?? [];
        const next = current.includes(option)
          ? current.filter((v) => v !== option)
          : [...current, option];
        const updated = { ...prev, [condition]: { ...prev[condition], [attrName]: next } };
        // Save with the updated state (can't rely on re-render before async)
        handleConditionSave(condition, updated);
        return updated;
      });
    },
    [handleConditionSave],
  );

  const completedCount = labelsToAssign.filter((cond) => {
    const attrDefs = attributeDefinitions[cond] ?? {};
    const attrNames = Object.keys(attrDefs);
    if (attrNames.length === 0) return false;
    const vals = localAttr[cond] ?? {};
    return attrNames.every((a) => (vals[a] ?? []).length > 0);
  }).length;

  const isMutating = saveMutation.isPending || deleteMutation.isPending;

  return (
    <div className="flex flex-col h-full bg-bg text-fg text-sm select-none">
      {/* Header */}
      <div className="px-3 pt-3 pb-2 border-b border-border shrink-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-fg-muted uppercase tracking-wider">
            Attribute Labeling
          </span>
          <div className="flex items-center gap-2">
            {onWorkList && <WorkListButton onClick={onWorkList} compact />}
            <span className="text-xs text-fg-muted">
              {currentIndex + 1} / {totalCases}
            </span>
          </div>
        </div>
        <div className="text-xs text-fg-muted truncate">{caseId.slice(0, 32)}…</div>
        <div className="mt-1.5 text-xs text-fg-muted">
          <span
            className={
              completedCount === labelsToAssign.length ? "text-signal-text-tint font-semibold" : ""
            }
          >
            {completedCount}/{labelsToAssign.length} complete
          </span>
        </div>
      </div>

      {/* Conditions */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-4">
        {labelsToAssign.length === 0 ? (
          <div className="text-fg-muted text-xs pt-4 text-center">
            No positive findings for this case
          </div>
        ) : (
          labelsToAssign.map((condition) => {
            const attrDefs = attributeDefinitions[condition] ?? {};
            const attrNames = Object.keys(attrDefs);
            const vals = localAttr[condition] ?? {};
            const allFilled =
              attrNames.length > 0 && attrNames.every((a) => (vals[a] ?? []).length > 0);
            const isSaving = saving[condition];

            return (
              <div
                key={condition}
                className={`rounded-lg border px-3 py-2.5 ${
                  allFilled ? "border-signal-stroke bg-signal-tint" : "border-border bg-surface"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-fg">{condition}</span>
                  {allFilled && (
                    <span className="text-[10px] text-signal-text-tint font-medium">
                      ✓ complete
                    </span>
                  )}
                  {isSaving && <span className="text-[10px] text-fg-muted">saving…</span>}
                </div>

                {attrNames.length === 0 ? (
                  <div className="text-[10px] text-fg-muted italic">No attributes defined</div>
                ) : (
                  <div className="space-y-3">
                    {attrNames.map((attrName) => {
                      const options = attrDefs[attrName] ?? [];
                      const selected = vals[attrName] ?? [];
                      return (
                        <div key={attrName}>
                          <div className="text-[10px] text-fg-muted mb-1">{attrName}</div>
                          <div className="flex flex-wrap gap-1">
                            {options.map((opt) => {
                              const checked = selected.includes(opt);
                              return (
                                <button
                                  key={opt}
                                  disabled={isMutating}
                                  onClick={() => handleToggleAndSave(condition, attrName, opt)}
                                  className={`px-2 py-0.5 rounded text-[10px] border transition-colors
                                    disabled:opacity-40 disabled:cursor-not-allowed
                                    ${
                                      checked
                                        ? "bg-signal-tint border-signal-stroke text-signal-text-tint"
                                        : "bg-surface-2 border-border text-fg-muted hover:border-border-strong hover:text-fg"
                                    }`}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Navigation */}
      <div className="px-3 pb-3 pt-2 border-t border-border shrink-0">
        <NavigationButtons
          onPrevious={onPrevious}
          onNext={onNext}
          onNextUnlabeled={onNextUnlabeled}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          hasNextUnlabeled={hasNextUnlabeled}
        />
      </div>
    </div>
  );
}
